/**
 * Color helpers for theme. Pure functions.
 */
import type { ThemeColors } from './store';

export function hexToHsl(hex: string): { h: number; s: number; l: number } {
  hex = hex.replace(/^#/, '');
  let r = 0,
    g = 0,
    b = 0;
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  } else if (hex.length === 6) {
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  }
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  const delta = max - min;
  let l = (max + min) / 2;
  let h = 0,
    s = 0;
  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    if (max === r) {
      h = ((g - b) / delta + (g < b ? 6 : 0)) % 6;
    } else if (max === g) {
      h = (b - r) / delta + 2;
    } else if (max === b) {
      h = (r - g) / delta + 4;
    }
    h *= 60;
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

export function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0,
    g = 0,
    b = 0;
  if (h >= 0 && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h >= 60 && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h >= 180 && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h >= 240 && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (h >= 300 && h < 360) {
    r = c;
    g = 0;
    b = x;
  }
  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

export function hexToRgba(hex: string, alpha: number): string {
  hex = hex.replace(/^#/, '');
  let r = 0,
    g = 0,
    b = 0;
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  } else if (hex.length === 6) {
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Solid mix of two hex colors. `t` = weight of `to` (0 = all `from`, 1 = all `to`).
 */
export function mixHex(from: string, to: string, t: number): string {
  const a = hexToRgbChannels(from);
  const b = hexToRgbChannels(to);
  const w = clamp(t, 0, 1);
  const r = Math.round(a.r * (1 - w) + b.r * w);
  const g = Math.round(a.g * (1 - w) + b.g * w);
  const bl = Math.round(a.b * (1 - w) + b.b * w);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${bl.toString(16).padStart(2, '0')}`;
}

/**
 * Solid stand-in for `rgba(color, alpha)` over a light/dark canvas.
 * Light → mix toward white; dark → mix toward black. `color_alpha` is the old translucent weight of the color.
 */
function solidWash(color_hex: string, color_alpha: number, scheme: 'light' | 'dark'): string {
  const canvas = scheme === 'light' ? '#ffffff' : '#000000';
  return mixHex(canvas, color_hex, color_alpha);
}

/** Former translucent brand weights — kept so solid washes match prior look on white/black. */
const WASH_ULTRA = 0.1;
const WASH_FADE = 0.25;
const WASH_HALF = 0.55;
const WASH_STATUS_FADE = 0.16;
const WASH_STATUS_ULTRA = 0.08;
const WASH_MACRO_ULTRA = 0.15;

export function generateShade(hex: string, lightness_mod: number, saturation_mod: number = 0): string {
  const hsl = hexToHsl(hex);
  const new_l = Math.min(Math.max(hsl.l + lightness_mod, 0), 100);
  const new_s = Math.min(Math.max(hsl.s + saturation_mod, 0), 100);
  return hslToHex(hsl.h, new_s, new_l);
}

export function createText(hex: string): string {
  if (!hex) {
    return '#000000';
  }
  const hsl = hexToHsl(hex);
  return hsl.l > 50 ? '#000000' : '#ffffff';
}

// ---------------------------------------------------------------------------
// Brand triplet → full light/dark ThemeColors (API supplies 3 brand hexes + optional macro overrides)
// ---------------------------------------------------------------------------

export type BrandTriplet = { primary: string; secondary: string; tertiary: string };

/** Default brand triplet (matches previous app defaults); API may override the same three only. */
export const DEFAULT_BRAND_TRIPLET: BrandTriplet = {
  primary: '#35858b',
  secondary: '#4fbdba',
  tertiary: '#082227',
};

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

function hexToRgbChannels(hex: string): { r: number; g: number; b: number } {
  const raw = hex.replace(/^#/, '');
  if (raw.length === 3) {
    return {
      r: parseInt(raw[0] + raw[0], 16),
      g: parseInt(raw[1] + raw[1], 16),
      b: parseInt(raw[2] + raw[2], 16),
    };
  }
  return {
    r: parseInt(raw.slice(0, 2), 16) || 0,
    g: parseInt(raw.slice(2, 4), 16) || 0,
    b: parseInt(raw.slice(4, 6), 16) || 0,
  };
}

function averageHue(hexes: [string, string, string]): number {
  let sx = 0;
  let sy = 0;
  for (const h of hexes) {
    const { h: hue } = hexToHsl(h);
    const rad = (hue * Math.PI) / 180;
    sx += Math.cos(rad);
    sy += Math.sin(rad);
  }
  sx /= hexes.length;
  sy /= hexes.length;
  let deg = (Math.atan2(sy, sx) * 180) / Math.PI;
  if (deg < 0) deg += 360;
  return Math.round(deg);
}

function avgSaturation(hexes: [string, string, string]): number {
  const sum = hexes.reduce((a, h) => a + hexToHsl(h).s, 0);
  return sum / hexes.length;
}

type BrandRole = {
  base: string;
  light: string;
  dark: string;
  pressed: string;
  fade: string;
  half: string;
  ultra: string;
  on: string;
};

function deriveBrandRole(baseHex: string, scheme: 'light' | 'dark'): BrandRole {
  const on = createText(baseHex);
  return {
    base: baseHex,
    light: generateShade(baseHex, 26),
    dark: generateShade(baseHex, -26),
    pressed: generateShade(baseHex, -14),
    // Solid washes: ultra ≈ almost canvas, fade ≈ soft tint, half ≈ mid mix (old rgba weights).
    fade: solidWash(baseHex, WASH_FADE, scheme),
    half: solidWash(baseHex, WASH_HALF, scheme),
    ultra: solidWash(baseHex, WASH_ULTRA, scheme),
    on,
  };
}

function deriveStatusFamily(
  hue: number,
  avgS: number,
  mainL: number,
  lightL: number,
  scheme: 'light' | 'dark',
): {
  main: string;
  light: string;
  fade: string;
  ultra: string;
  text: string;
  on: string;
} {
  const sMain = clamp(Math.round(avgS + 18), 55, 92);
  const sLight = clamp(Math.round(avgS + 12), 50, 88);
  const main = hslToHex(hue, sMain, mainL);
  const light = hslToHex(hue, sLight, lightL);
  return {
    main,
    light,
    fade: solidWash(main, WASH_STATUS_FADE, scheme),
    ultra: solidWash(main, WASH_STATUS_ULTRA, scheme),
    text: createText(main),
    on: createText(main),
  };
}

function buildSemanticLight(triplet: BrandTriplet, t: [string, string, string]) {
  const hAvg = averageHue(t);
  const hpm = averageHue([triplet.primary, triplet.primary, triplet.primary]);
  const th = hexToHsl(triplet.tertiary);
  const bg_s = clamp(avgSaturation(t) * 0.35 + 6, 6, 18);

  // Darkest → lightest: background < backgroundElevated < surfaceElevated < surface
  const background = hslToHex(hAvg, bg_s, 95);
  const backgroundElevated = hslToHex(hAvg, clamp(bg_s - 2, 4, 14), 97);
  const surfaceElevated = hslToHex(hAvg, 4, 98);
  const surface = hslToHex(hpm, 2, 100);
  const surfaceLight = hslToHex(hAvg, 10, 96);
  const divider = hslToHex(hAvg, 10, 78);
  const skeleton = hslToHex(hAvg, 8, 90);
  const border = hslToHex(hAvg, 11, 72);

  const text = hslToHex(th.h, clamp(th.s * 0.45 + 6, 6, 18), 11);
  const textSecondary = hslToHex(th.h, clamp(th.s * 0.35 + 4, 4, 14), 34);
  const textLight = hslToHex(th.h, clamp(th.s * 0.28 + 3, 3, 12), 46);

  return {
    background,
    backgroundElevated,
    backgroundFade: solidWash(background, 0.92, 'light'),
    backdrop: hexToRgba(hslToHex(hAvg, 15, 8), 0.45),
    surface,
    surfaceElevated,
    surfaceLight,
    surfaceFade: solidWash(surface, 0.9, 'light'),
    divider,
    skeleton,
    text,
    textSecondary,
    textLight,
    textInverse: '#ffffff',
    border,
    borderLight: solidWash(border, 0.22, 'light'),
    borderSubtle: solidWash(border, 0.18, 'light'),
    shadowColor: hslToHex(hAvg, 12, 10),
    disabled: hslToHex(hAvg, 5, 88),
    disabledLight: hslToHex(hAvg, 4, 94),
    disabledDark: hslToHex(hAvg, 6, 62),
    grey: hslToHex(hAvg, 5, 55),
    greyLight: hslToHex(hAvg, 4, 72),
    greyDark: hslToHex(hAvg, 6, 38),
  };
}

function buildSemanticDark(triplet: BrandTriplet, t: [string, string, string]) {
  const hAvg = averageHue(t);
  const hpm = averageHue([triplet.primary, triplet.primary, triplet.primary]);
  // Darkest → lightest: background < backgroundElevated < surfaceElevated < surface
  const background = hslToHex(hAvg, 8, 7);
  const backgroundElevated = hslToHex(hAvg, 8, 10);
  const surfaceElevated = hslToHex(hAvg, 7, 13);
  const surface = hslToHex(hpm, 9, 16);
  const surfaceLight = hslToHex(hAvg, 8, 12);
  const divider = hslToHex(hAvg, 6, 26);
  const skeleton = hslToHex(hAvg, 7, 14);
  const border = hslToHex(hAvg, 8, 30);

  const text = hslToHex(hAvg, 6, 95);
  const textSecondary = hslToHex(hAvg, 5, 72);
  const textLight = hslToHex(hAvg, 5, 55);
  const textInverse = hslToHex(hAvg, 8, 12);

  return {
    background,
    backgroundElevated,
    backgroundFade: solidWash(background, 0.94, 'dark'),
    backdrop: hexToRgba('#000000', 0.62),
    surface,
    surfaceElevated,
    surfaceLight,
    surfaceFade: solidWash(surface, 0.9, 'dark'),
    divider,
    skeleton,
    text,
    textSecondary,
    textLight,
    textInverse,
    border,
    borderLight: solidWash(text, 0.14, 'dark'),
    borderSubtle: solidWash(text, 0.1, 'dark'),
    shadowColor: '#000000',
    disabled: hslToHex(hAvg, 6, 22),
    disabledLight: hslToHex(hAvg, 7, 28),
    disabledDark: hslToHex(hAvg, 5, 48),
    grey: hslToHex(hAvg, 4, 48),
    greyLight: hslToHex(hAvg, 4, 62),
    greyDark: hslToHex(hAvg, 5, 34),
  };
}

function buildMacrosFromBrands(triplet: BrandTriplet, avgS: number, scheme: 'light' | 'dark') {
  const ph = hexToHsl(triplet.primary).h;
  const sh = hexToHsl(triplet.secondary).h;
  const th = hexToHsl(triplet.tertiary).h;
  const mix = (h: number, sBoost: number, l: number) =>
    hslToHex(h, clamp(Math.round(avgS + sBoost), 45, 92), l);

  const calorie = mix(ph, 25, 52);
  const protein = mix(sh, 20, 48);
  const carbs = mix(th, 22, 50);
  const fatsH = (ph + 42) % 360;
  const fats = mix(fatsH, 28, 50);

  return {
    calorie,
    protein,
    carbs,
    fats,
    calorieUltra: solidWash(calorie, WASH_MACRO_ULTRA, scheme),
    proteinUltra: solidWash(protein, WASH_MACRO_ULTRA, scheme),
    carbsUltra: solidWash(carbs, WASH_MACRO_ULTRA, scheme),
    fatsUltra: solidWash(fats, WASH_MACRO_ULTRA, scheme),
  };
}

export type BuildFullThemeOptions = {
  /** Override generated macro colors (from API). */
  macroOverrides?: Partial<Pick<ThemeColors, 'calorie' | 'protein' | 'carbs' | 'fats'>>;
};

/**
 * Full palette from three brand hex codes + light/dark.
 * Roles follow API keys as-is (primary → primary, secondary → secondary, tertiary → tertiary).
 * Semantic surfaces/text derive from the triplet; status colors use fixed hues;
 * macros derive from triplet unless overridden.
 */
export function buildFullThemeColors(
  triplet: BrandTriplet,
  scheme: 'light' | 'dark',
  options?: BuildFullThemeOptions,
): ThemeColors {
  const t: [string, string, string] = [triplet.primary, triplet.secondary, triplet.tertiary];
  const avgS = avgSaturation(t);

  const p = deriveBrandRole(triplet.primary, scheme);
  const s = deriveBrandRole(triplet.secondary, scheme);
  const ter = deriveBrandRole(triplet.tertiary, scheme);

  const err = deriveStatusFamily(0, avgS, 66, 74, scheme);
  const ok = deriveStatusFamily(142, avgS, 40, 58, scheme);
  const warn = deriveStatusFamily(36, avgS, 48, 62, scheme);
  const inf = deriveStatusFamily(210, avgS, 44, 60, scheme);

  const semantic = scheme === 'dark' ? buildSemanticDark(triplet, t) : buildSemanticLight(triplet, t);
  let macros = buildMacrosFromBrands(triplet, avgS, scheme);
  const o = options?.macroOverrides;
  if (o?.calorie) {
    macros.calorie = o.calorie;
    macros.calorieUltra = solidWash(o.calorie, WASH_MACRO_ULTRA, scheme);
  }
  if (o?.protein) {
    macros.protein = o.protein;
    macros.proteinUltra = solidWash(o.protein, WASH_MACRO_ULTRA, scheme);
  }
  if (o?.carbs) {
    macros.carbs = o.carbs;
    macros.carbsUltra = solidWash(o.carbs, WASH_MACRO_ULTRA, scheme);
  }
  if (o?.fats) {
    macros.fats = o.fats;
    macros.fatsUltra = solidWash(o.fats, WASH_MACRO_ULTRA, scheme);
  }

  const base: ThemeColors = {
    primary: p.base,
    primaryLight: p.light,
    primaryDark: p.dark,
    primaryPressed: p.pressed,
    primaryFade: p.fade,
    primaryHalf: p.half,
    primaryUltra: p.ultra,
    primaryText: p.on,
    onPrimary: p.on,

    secondary: s.base,
    secondaryLight: s.light,
    secondaryDark: s.dark,
    secondaryPressed: s.pressed,
    secondaryFade: s.fade,
    secondaryHalf: s.half,
    secondaryUltra: s.ultra,
    secondaryText: s.on,
    onSecondary: s.on,

    tertiary: ter.base,
    tertiaryLight: ter.light,
    tertiaryDark: ter.dark,
    tertiaryPressed: ter.pressed,
    tertiaryFade: ter.fade,
    tertiaryHalf: ter.half,
    tertiaryUltra: ter.ultra,
    tertiaryText: ter.on,
    onTertiary: ter.on,

    ...semantic,

    error: err.main,
    errorLight: err.light,
    errorFade: err.fade,
    errorText: err.text,
    onError: err.on,
    errorUltra: err.ultra,

    success: ok.main,
    successLight: ok.light,
    successFade: ok.fade,
    successText: ok.text,
    onSuccess: ok.on,
    successUltra: ok.ultra,

    warning: warn.main,
    warningLight: warn.light,
    warningFade: solidWash(warn.main, WASH_FADE, scheme),
    warningText: createText(warn.main),
    onWarning: createText(warn.main),
    warningUltra: warn.ultra,

    alert: inf.main,
    alertLight: inf.light,
    alertFade: inf.fade,
    alertText: inf.text,
    onInfo: inf.on,
    alertUltra: inf.ultra,

    danger: err.main,

    white: '#ffffff',
    black: '#000000',

    ...macros,
  };

  return base;
}
